// Renders section-grouped fabric swatches from productSwatch data
// when Globo's swatch picker DOM is ready. Links proxy to original
// Globo <li> elements via data-value matching, without owning variant selection.
(function () {
  function debounce(fn, delay) {
    var timer = null;
    return function () {
      var context = this;
      var args = arguments;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
        timer = null;
      }, delay);
    };
  }

  // The zero-based index of the Fabric option within the ' / '-separated variant
  // title. Resolved once from the Globo swatch containers (whichever container's
  // legend data-name contains "Fabric"). Falls back to the last segment when it
  // cannot be determined, preserving previous behaviour.
  // This makes all title parsing generic regardless of the Fabric option's
  // position (first, middle, or last).
  var _fabricOptionIndex = null;

  // Discover the Fabric option's position by inspecting the order of Globo's
  // option containers. Each 'div.swatch--gl.clearfix' maps to one product option
  // (in option order) and exposes its name via legend.name-option[data-name].
  // Returns the index of the container whose name contains "Fabric", or -1.
  function detectFabricOptionIndex() {
    var containers = document.querySelectorAll('div.swatch--gl.clearfix');
    if (!containers || containers.length === 0) return -1;
    for (var i = 0; i < containers.length; i++) {
      var legend = containers[i].querySelector('legend.name-option');
      var optionName = legend ? legend.getAttribute('data-name') : '';
      if (optionName && optionName.indexOf('Fabric') !== -1) {
        return i;
      }
    }
    return -1;
  }

  // Resolve (and cache) the Fabric option index. Only a valid (>= 0) index is
  // cached so a premature call before Globo's DOM is ready does not permanently
  // pin the value to -1. Any negative/unknown result is treated by callers as
  // "fabric is the last segment" for backward compat.
  function getFabricOptionIndex() {
    if (_fabricOptionIndex === null || _fabricOptionIndex < 0) {
      var idx = detectFabricOptionIndex();
      if (idx >= 0) {
        _fabricOptionIndex = idx;
      }
      return idx;
    }
    return _fabricOptionIndex;
  }

  // Given a variant title like '75" / Natural Wood / Ivory Chateau Basketweave',
  // returns the option prefix consisting of every NON-fabric segment joined by
  // ' / ' in their original order. Works regardless of the fabric option's
  // position (e.g., fabric first: 'Ivory... / 75" / Natural Wood' -> '75" / Natural Wood').
  function getOptionPrefix(title) {
    if (!title) return '';
    var parts = title.split(' / ');
    if (parts.length <= 1) return '';
    var fabricIdx = getFabricOptionIndex();
    // Fallback: fabric is the last segment when index is unknown.
    if (fabricIdx < 0 || fabricIdx >= parts.length) {
      fabricIdx = parts.length - 1;
    }
    var prefixParts = [];
    for (var i = 0; i < parts.length; i++) {
      if (i === fabricIdx) continue;
      prefixParts.push(parts[i]);
    }
    return prefixParts.join(' / ');
  }

  // Given a variant title like '75" / Natural Wood / Ivory Chateau Basketweave',
  // returns the fabric name segment based on the detected fabric option index.
  // Falls back to the last segment when the index is unknown.
  // Used as a fallback when variant-level fabric_swatch_item metafield is not populated.
  function getFabricNameFromTitle(title) {
    if (!title) return '';
    var parts = title.split(' / ');
    var fabricIdx = getFabricOptionIndex();
    if (fabricIdx < 0 || fabricIdx >= parts.length) {
      fabricIdx = parts.length - 1;
    }
    return parts[fabricIdx] || '';
  }

  // Read the currently selected variant title from Shopify's
  // <script type="application/json" data-selected-variant>, which
  // the theme keeps updated on every variant change.
  function getSelectedVariantTitle() {
    var script = document.querySelector('script[data-selected-variant]');
    if (!script) return null;
    try {
      var data = JSON.parse(script.textContent);
      return data.title || null;
    } catch {
      return null;
    }
  }

  // Read the currently selected option values from Globo's swatch containers,
  // excluding whichever container is the Fabric option (identified by name, not
  // by position), and join them with " / " to form the option prefix.
  // Containers are iterated in their DOM order, which matches the product's
  // option order and therefore the order of the non-fabric segments produced by
  // getOptionPrefix(). This keeps prefixes comparable regardless of whether the
  // Fabric option is first, middle, or last.
  // Falls back to getSelectedVariantTitle() if no Globo containers are found.
  function getSelectedOptionPrefix() {
    var containers = document.querySelectorAll('div.swatch--gl.clearfix');
    if (!containers || containers.length === 0) {
      var title = getSelectedVariantTitle();
      return title ? getOptionPrefix(title) : '';
    }

    var parts = [];
    containers.forEach(function (container) {
      var legend = container.querySelector('legend.name-option');
      if (!legend) return;
      var optionName = legend.getAttribute('data-name');
      // Skip the Fabric option wherever it appears in the option order.
      if (optionName && optionName.indexOf('Fabric') !== -1) return;

      var selectedLi = container.querySelector('li.globo-selected, li.selected, li.active');
      if (!selectedLi) {
        var inp = container.querySelector('li input:checked');
        if (inp) selectedLi = inp.closest('li');
      }
      if (selectedLi) {
        var val = selectedLi.getAttribute('data-value');
        if (val) parts.push(val);
      }
    });

    return parts.join(' / ');
  }

  var _mtoState = {
    stateObserver: null,
    variantUnsubscriber: null,
  };

  function createSwatchSections() {
    const sectionsContainer = document.querySelector(
      'div.swatch--gl.clearfix:has(legend.name-option[data-name*="Fabric"])',
    );

    if (!sectionsContainer) {
      return;
    }
    // Guard against double initialization (first-time only)
    if (sectionsContainer.dataset.mtoProxyBuilt === 'true') {
      return;
    }

    const originalUl = sectionsContainer.querySelector('ul');
    if (!originalUl) {
      return;
    }

    const variantSwatchesScript = document.querySelector('script[data-variant-swatches]');
    if (!variantSwatchesScript) {
      return;
    }

    let variantData;
    try {
      variantData = JSON.parse(variantSwatchesScript.textContent);
    } catch (e) {
      console.error('Failed to parse variant swatches data:', e);
      return;
    }

    const unique_section_names = variantData.unique_section_names;

    // Build a fabric_name → array of variant entries lookup
    // Multiple variants share the same fabric_name (e.g., "Ivory Chateau Basketweave"
    // exists for 75"/Natural Wood, 95"/Brass, etc.).
    // At hover time we match the current option prefix to get the right variant's details.
    // Fabric name is read from variant-level metafield if available, else derived from title.
    const fabricNameToVariants = {};
    Object.keys(variantData).forEach(function (key) {
      if (key === 'unique_section_names') return;
      var entry = variantData[key];
      if (!entry || !entry.title) return;
      var fabricName = getFabricNameFromTitle(entry.title);
      if (!fabricName) return;
      if (!fabricNameToVariants[fabricName]) {
        fabricNameToVariants[fabricName] = [];
      }
      fabricNameToVariants[fabricName].push(entry);
    });

    // Get swatch details for the hovered fabric, matching the current option prefix
    function getCurrentHoverSwatch(fabricName) {
      var variants = fabricNameToVariants[fabricName];
      if (!variants || variants.length === 0) return null;

      // Common case: single variant for this fabric
      if (variants.length === 1) return extractSwatch(variants[0]);

      // Multiple variants — match current prefix from selected option values
      var prefix = getSelectedOptionPrefix();

      if (prefix) {
        for (var i = 0; i < variants.length; i++) {
          if (getOptionPrefix(variants[i].title) === prefix) {
            return extractSwatch(variants[i]);
          }
        }
      }

      // Fallback: first variant
      return extractSwatch(variants[0]);
    }

    function extractSwatch(entry) {
      var swatch = entry.swatch;
      return {
        fabric_name: swatch ? swatch.fabric_name || swatch.fabricName || '' : getFabricNameFromTitle(entry.title),
        material_composition: swatch ? swatch.material_composition || swatch.materialComposition || '' : '',
        feature_tags: swatch ? swatch.feature_tags || swatch.featureTags || [] : [],
        care_instructions: swatch ? swatch.care_instructions || swatch.careInstructions || '' : '',
        hover_image_url: entry.hover_image_url || '',
      };
    }

    // Build a lookup of original <li> elements by data-value (fabric_name)
    const originalLiItems = originalUl.querySelectorAll('li');
    const originalByValue = {};
    originalLiItems.forEach(function (li) {
      var val = li.getAttribute('data-value');
      if (val) {
        originalByValue[val] = li;
      }
    });
    // Collect unique fabrics from variant entries matching the current option prefix
    var currentPrefix = getSelectedOptionPrefix();

    var fabrics = {};
    Object.keys(variantData).forEach(function (key) {
      if (key === 'unique_section_names') return;
      var entry = variantData[key];
      if (!entry || !entry.title) return;
      if (currentPrefix && getOptionPrefix(entry.title) !== currentPrefix) return;

      var fabricName = getFabricNameFromTitle(entry.title);
      if (!fabricName || fabrics[fabricName]) return;

      fabrics[fabricName] = {
        fabric_name: fabricName,
        swatch_image_url: entry.swatch_image_url || '',
        section_name: entry.section.section_name || 'Unsectioned',
        section_sort_order: entry.section.sort_order || 0,
        sort_order: entry.swatch.sort_order || 0,
      };
    });

    var fabricList = Object.keys(fabrics).map(function (n) {
      return fabrics[n];
    });
    if (fabricList.length === 0) return;

    // Group by section_name
    var grouped = {};
    fabricList.forEach(function (f) {
      if (!grouped[f.section_name]) grouped[f.section_name] = [];
      grouped[f.section_name].push(f);
    });

    // Sort within each group by sort_order
    Object.keys(grouped).forEach(function (key) {
      grouped[key].sort(function (a, b) {
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
    });

    // Sort sections: if unique_section_names exists, use it; else sort by section_sort_order
    var sortedSections;
    if (unique_section_names && Array.isArray(unique_section_names) && unique_section_names.length > 0) {
      var sectionSortOrder = {};
      fabricList.forEach(function (f) {
        if (!(f.section_name in sectionSortOrder)) {
          sectionSortOrder[f.section_name] = f.section_sort_order || 0;
        }
      });
      sortedSections = unique_section_names.slice().sort(function (a, b) {
        return (sectionSortOrder[a] || 0) - (sectionSortOrder[b] || 0);
      });
      Object.keys(grouped).forEach(function (name) {
        if (sortedSections.indexOf(name) === -1 && grouped[name].length > 0) {
          sortedSections.push(name);
        }
      });
    } else {
      sortedSections = Object.keys(grouped);
    }

    // Move "Unsectioned" to the end
    var unsectionedIdx = sortedSections.indexOf('Unsectioned');
    if (unsectionedIdx !== -1) {
      sortedSections.splice(unsectionedIdx, 1);
      sortedSections.push('Unsectioned');
    }

    // Helpers for syncing state between original <li> and proxy buttons
    function syncProxyActiveState() {
      const proxyButtons = proxyRoot.querySelectorAll('.mto-swatch-proxy-item');
      proxyButtons.forEach(function (btn) {
        const original = btn._mtoOriginalLi;
        const isActive =
          original &&
          (original.classList.contains('globo-selected') ||
            original.classList.contains('selected') ||
            original.classList.contains('active') ||
            !!original.querySelector('input:checked'));
        btn.classList.toggle('is-active', !!isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    function syncProxyOutOfStockState() {
      const proxyButtons = proxyRoot.querySelectorAll('.mto-swatch-proxy-item');
      proxyButtons.forEach(function (btn) {
        const original = btn._mtoOriginalLi;
        const proxyLi = btn.closest('.mto-swatch-proxy-li');
        if (!original || !proxyLi) {
          return;
        }
        const isOutOfStock = original.classList.contains('globo-out-of-stock');
        proxyLi.classList.toggle('mto-swatch-out-of-stock', isOutOfStock);
        proxyLi.setAttribute('aria-hidden', isOutOfStock ? 'true' : 'false');
      });
    }

    function triggerOriginalSelection(originalLi) {
      if (!originalLi) {
        return;
      }
      const input = originalLi.querySelector('input');
      const clickTarget =
        originalLi.querySelector('label') ||
        originalLi.querySelector('a.swatch-anchor') ||
        originalLi.querySelector('a') ||
        originalLi;

      // On iOS Safari, clicking the label/anchor is sufficient and will trigger
      // the input events automatically. Only manually dispatch events if click doesn't work.
      if (clickTarget && typeof clickTarget.click === 'function') {
        clickTarget.click();
        
        // Check if click successfully updated the input state
        if (input && input.checked) {
          // Click worked, no need for manual event dispatch
          window.requestAnimationFrame(syncProxyActiveState);
          return;
        }
      }
      
      // Fallback: manually update input state if click didn't work
      if (input) {
        if (!input.checked) {
          input.checked = true;
        }
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      window.requestAnimationFrame(syncProxyActiveState);
    }

    // Build the proxy UI
    const proxyRoot = document.createElement('div');
    proxyRoot.className = 'mto-swatch-proxy';

    sortedSections.forEach(function (sectionName) {
      const items = grouped[sectionName];
      if (!items || items.length === 0) {
        return;
      }

      const sectionDiv = document.createElement('div');
      sectionDiv.className = 'mto-swatch-section-container';

      const heading = document.createElement('div');
      heading.textContent = sectionName;
      heading.className = 'mto-swatch-section-heading';

      const ul = document.createElement('ul');
      ul.className = 'value mto-swatch-section';

      items.forEach(function (swatch) {
        const originalLi = originalByValue[swatch.fabric_name] || null;
        const isOutOfStock = originalLi && originalLi.classList.contains('globo-out-of-stock');
        const isUnlinked = !originalLi;

        const proxyLi = document.createElement('li');
        proxyLi.className = 'mto-swatch-proxy-li';
        if (isOutOfStock) {
          proxyLi.classList.add('mto-swatch-out-of-stock');
        }
        if (isUnlinked) {
          proxyLi.classList.add('mto-swatch-proxy-unlinked');
        }

        const proxyBtn = document.createElement('button');
        proxyBtn.type = 'button';
        proxyBtn.className = 'mto-swatch-proxy-item';
        proxyBtn.setAttribute('aria-label', swatch.fabric_name || '');
        proxyBtn.setAttribute('title', swatch.fabric_name || '');
        proxyBtn.setAttribute('aria-pressed', 'false');
        proxyBtn._mtoOriginalLi = originalLi;

        if (swatch.swatch_image_url) {
          var proxyImg = document.createElement('img');
          proxyImg.src = swatch.swatch_image_url;
          proxyImg.alt = swatch.fabric_name || 'Fabric swatch';
          proxyImg.className = 'mto-swatch-proxy-image';
          proxyBtn.appendChild(proxyImg);
        } else {
          var placeholder = document.createElement('span');
          placeholder.className = 'mto-swatch-proxy-placeholder';
          placeholder.setAttribute('aria-hidden', 'true');
          proxyBtn.appendChild(placeholder);
        }

        // Only attach click handler when linked to an original Globo <li>
        if (!isUnlinked) {
          proxyBtn.addEventListener('click', function (event) {
            event.preventDefault();
            // Remove popover when swatch is clicked to prevent showing outdated data
            if (hoverPopover) {
              removeHoverPopover();
            }
            triggerOriginalSelection(originalLi);
          });
        }

        // Attach leave handler to each swatch button
        attachSwatchLeaveHandler(proxyBtn);

        proxyLi.appendChild(proxyBtn);
        ul.appendChild(proxyLi);
      });

      sectionDiv.appendChild(heading);
      sectionDiv.appendChild(ul);
      proxyRoot.appendChild(sectionDiv);
    });

    // Hide the original Globo <ul> visually
    originalUl.classList.add('mto-swatch-original-hidden');

    // Insert the proxy UI right after the original <ul>
    originalUl.insertAdjacentElement('afterend', proxyRoot);

    // Sync states immediately
    syncProxyActiveState();
    syncProxyOutOfStockState();

    // ---------- Hover popover for fabric details ----------
    var hoverPopover = null;

    function removeHoverPopover() {
      // Remove tracked popover
      if (hoverPopover) {
        try {
          hoverPopover.remove();
        } catch {
          // Popover may already be removed from DOM
        }
        hoverPopover = null;
      }
      // Clean up any orphaned popovers that may exist in the DOM
      var orphanedPopovers = document.querySelectorAll('.mto-swatch-popover');
      orphanedPopovers.forEach(function (pop) {
        try {
          pop.remove();
        } catch {
          // Ignore removal errors
        }
      });
    }

    function escHtml(str) {
      return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function buildHoverPopover(swatch) {
      var pop = document.createElement('div');
      pop.className = 'mto-swatch-popover';
      var html = '';
      if (swatch.hover_image_url) {
        html +=
          '<div class="mto-swatch-popover__image"><img src="' +
          escHtml(swatch.hover_image_url) +
          '" alt="' +
          escHtml(swatch.fabric_name || '') +
          '"></div>';
      }
      html += '<div class="mto-swatch-popover__name">' + escHtml(swatch.fabric_name || '') + '</div>';
      if (swatch.material_composition) {
        html += '<div class="mto-swatch-popover__composition">' + escHtml(swatch.material_composition) + '</div>';
      }
      if (swatch.feature_tags && swatch.feature_tags.length) {
        html += '<div class="mto-swatch-popover__tags">';
        swatch.feature_tags.forEach(function (tag) {
          html += '<span class="mto-swatch-popover__tag">' + escHtml(tag) + '</span>';
        });
        html += '</div>';
      }
      if (swatch.care_instructions) {
        html += '<div class="mto-swatch-popover__care">Care: ' + escHtml(swatch.care_instructions) + '</div>';
      }
      pop.innerHTML = html;
      return pop;
    }

    // Add mouseleave handler to each swatch button to remove popover when mouse leaves
    function attachSwatchLeaveHandler(btn) {
      btn.addEventListener('pointerleave', function (e) {
        var related = e.relatedTarget;
        // Don't remove if moving to the popover
        if (related && hoverPopover && (hoverPopover.contains(related) || hoverPopover === related)) {
          return;
        }
        // Remove popover when leaving the swatch
        removeHoverPopover();
      });
    }

    proxyRoot.addEventListener('pointerover', function (e) {
      if (window.matchMedia('(max-width:1080px)').matches) return;
      var btn = e.target.closest('.mto-swatch-proxy-item');
      if (!btn) {
        // If we're not over a button, check if we left the swatch area entirely
        if (!e.target.closest('.mto-swatch-proxy')) {
          removeHoverPopover();
        }
        return;
      }
      /* already showing for this button — no-op */
      if (hoverPopover && hoverPopover._for === btn) return;

      // Always clean up before creating new popover
      removeHoverPopover();

      var fabricName = btn.getAttribute('aria-label');
      var swatch = getCurrentHoverSwatch(fabricName);
      if (!swatch) {
        removeHoverPopover();
        return;
      }

      var pop = buildHoverPopover(swatch);
      pop._for = btn;

      // Ensure any existing popovers are removed before appending
      removeHoverPopover();

      // Append to body instead of button to escape stacking context
      document.body.appendChild(pop);
      hoverPopover = pop;

      // Position popover dynamically based on viewport space using fixed positioning
      window.requestAnimationFrame(function () {
        var rect = btn.getBoundingClientRect();
        var popRect = pop.getBoundingClientRect();
        var spaceAbove = rect.top;
        var spaceBelow = window.innerHeight - rect.bottom;
        var popHeight = popRect.height;
        var popWidth = popRect.width;

        // Calculate horizontal position (centered on button)
        var left = rect.left + rect.width / 2 - popWidth / 2;
        // Ensure popover stays within viewport
        left = Math.max(10, Math.min(left, window.innerWidth - popWidth - 10));

        // If not enough space above, position below
        var top = 0;
        if (spaceAbove < popHeight + 20 && spaceBelow > spaceAbove) {
          pop.classList.add('mto-swatch-popover--below');
          top = rect.bottom + 14;
          pop.style.top = top + 'px';
        } else {
          pop.classList.remove('mto-swatch-popover--below');
          top = rect.top - popHeight - 14;
          pop.style.top = top + 'px';
        }

        pop.style.left = left + 'px';
      });
    });

    proxyRoot.addEventListener('pointerleave', function (e) {
      if (window.matchMedia('(max-width:1080px)').matches) return;
      var related = e.relatedTarget;
      // Only keep popover open if moving to the popover itself
      if (related && hoverPopover && (hoverPopover.contains(related) || hoverPopover === related)) {
        return;
      }
      removeHoverPopover();
    });

    // Monitor pointer movement to remove popover when leaving both swatch and popover areas
    document.addEventListener(
      'pointermove',
      function (e) {
        if (!hoverPopover) return;
        if (window.matchMedia('(max-width:1080px)').matches) {
          removeHoverPopover();
          return;
        }

        // Check if pointer is over swatch area or popover
        var isOverSwatch = e.target.closest('.mto-swatch-proxy');
        var isOverPopover = e.target === hoverPopover || hoverPopover.contains(e.target);

        // Remove popover if pointer is not over swatch area or popover
        if (!isOverSwatch && !isOverPopover) {
          removeHoverPopover();
        }
      },
      { passive: true },
    );

    // Remove popover when user scrolls
    window.addEventListener(
      'scroll',
      function () {
        if (hoverPopover) {
          removeHoverPopover();
        }
      },
      { passive: true },
    );

    // Keep proxy in sync when Globo mutates the originals
    function onStateChange() {
      syncProxyActiveState();
      syncProxyOutOfStockState();
    }

    _mtoState.stateObserver = new MutationObserver(onStateChange);
    _mtoState.stateObserver.observe(originalUl, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
      childList: true,
    });
    originalUl.addEventListener('change', onStateChange);
    sectionsContainer._mtoChangeHandler = onStateChange;

    // Watch for variant changes that affect which swatches should appear.
    // Subscribes to the theme's variantChange event, which fires after the
    // DOM has been updated with the new variant data (including
    // script[data-selected-variant]).
    if (!_mtoState.variantUnsubscriber) {
      // eslint-disable-next-line no-undef
      _mtoState.variantUnsubscriber = subscribe(
        // eslint-disable-next-line no-undef
        PUB_SUB_EVENTS.variantChange,
        debounce(function (event) {
          var variant = event && event.data && event.data.variant;
          rebuildProxy(sectionsContainer, variant);
        }, 150),
      );
    }

    sectionsContainer.dataset.mtoProxyBuilt = 'true';
  }

  function rebuildProxy(sectionsContainer, variant) {
    if (!sectionsContainer) return;

    // Tear down existing proxy, observers, and event listeners
    if (_mtoState.stateObserver) {
      _mtoState.stateObserver.disconnect();
      _mtoState.stateObserver = null;
    }
    if (sectionsContainer._mtoChangeHandler) {
      var prevUl = sectionsContainer.querySelector('ul');
      if (prevUl) prevUl.removeEventListener('change', sectionsContainer._mtoChangeHandler);
      sectionsContainer._mtoChangeHandler = null;
    }
    var oldProxy = sectionsContainer.querySelector('.mto-swatch-proxy');
    if (oldProxy) oldProxy.remove();
    var origUl = sectionsContainer.querySelector('ul');
    if (origUl) origUl.classList.remove('mto-swatch-original-hidden');

    delete sectionsContainer.dataset.mtoProxyBuilt;
    createSwatchSections(variant);
  }

  // Check if Globo element already exists
  const existingElem = document.querySelector('.globo-swatch-product-detail');
  if (existingElem) {
    createSwatchSections();
    return;
  }

  // Wait for Globo to inject its DOM
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1 && node.classList && node.classList.contains('globo-swatch-product-detail')) {
            createSwatchSections();
            observer.disconnect();
          } else if (node.nodeType === 1 && node.querySelector) {
            const foundElem = node.querySelector('.globo-swatch-product-detail');
            if (foundElem) {
              createSwatchSections();
              observer.disconnect();
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
